import { logger } from '../../utils/logger';
/**
 * Dashboard Settings Slice
 * Redux Toolkit slice for dashboard user preferences (date range, etc.)
 *
 * Separate from dashboardSlice which handles widget layout.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { DateRange } from '../../types/dashboard';

/**
 * Constants
 */
const SETTINGS_STORAGE_KEY = 'dashboard_settings';

/**
 * Dashboard Settings State
 */
export interface DashboardSettingsState {
  dateRange: DateRange;
}

/**
 * Load settings from localStorage
 */
const loadSettingsFromStorage = (): DashboardSettingsState | null => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    logger.error('[DashboardSettings] Failed to load from storage:', error);
    return null;
  }
};

/**
 * Save settings to localStorage
 */
const saveSettingsToStorage = (state: DashboardSettingsState): void => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    logger.error('[DashboardSettings] Failed to save to storage:', error);
  }
};

/**
 * Initial State
 */
const storedSettings = loadSettingsFromStorage();
const initialState: DashboardSettingsState = storedSettings || {
  dateRange: 'max',
};

/**
 * Dashboard Settings Slice
 */
const dashboardSettingsSlice = createSlice({
  name: 'dashboardSettings',
  initialState,
  reducers: {
    /**
     * Set date range and persist to localStorage
     */
    setDateRange(state, action: PayloadAction<DateRange>) {
      state.dateRange = action.payload;
      saveSettingsToStorage(state);
    },

    /**
     * Reset settings to defaults
     */
    resetSettings() {
      const defaultState: DashboardSettingsState = { dateRange: 'max' };
      saveSettingsToStorage(defaultState);
      return defaultState;
    },
  },
});

/**
 * Selectors
 */
export const selectDateRange = (state: RootState): DateRange => state.dashboardSettings.dateRange;

/**
 * Export actions and reducer
 */
export const { setDateRange, resetSettings } = dashboardSettingsSlice.actions;

export default dashboardSettingsSlice.reducer;
